import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { api, fmtDateTime, money } from "@/lib/api";
import { useL, pick } from "@/lib/i18n";
import { KeycardPending } from "@/components/member/Keycard";
import { LangToggle } from "@/components/site/Navbar";

function Box({ icon, text, testId }) {
  const { t } = useLang();
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#08080a] px-4">
      <div className="panel brackets max-w-md p-8 text-center" data-testid={testId}>
        <div className="mb-4 flex justify-end"><LangToggle id="payment-box-lang-toggle" /></div>
        <div className="mb-4 flex justify-center">{icon}</div>
        <p className="text-lg text-white">{text}</p>
        <Link to="/member" className="btn-red mt-6 inline-block rounded-full px-6 py-3 font-semibold" data-testid="payment-back-btn">{t("pay.back")}</Link>
      </div>
    </div>
  );
}

function PaidDetails() {
  const { lang } = useLang();
  const L = useL();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/member/dashboard").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  if (!d) return d === false ? <Box testId="payment-success" icon={<CheckCircle2 className="text-emerald-400" size={40} />} text={L("Paiement confirmé.", "Payment confirmed.")} /> : null;
  const u = d.user;
  const row = (k, v, id) => <p className="flex justify-between gap-4 border-b border-white/5 py-2 text-sm"><span className="text-slate-400">{k}</span><span className="text-right text-white" data-testid={id}>{v}</span></p>;
  return (
    <div className="min-h-screen bg-[#08080a] px-4 py-12">
      <div className="mx-auto max-w-2xl" data-testid="payment-success">
        <div className="mb-6 flex justify-end"><LangToggle id="payment-lang-toggle" /></div>
        <p className="mb-4 flex items-center gap-2 text-lg text-emerald-300"><CheckCircle2 size={24} />{L("Paiement confirmé", "Payment confirmed")}</p>
        <div className="panel mb-6 p-5" data-testid="payment-details">
          {row(L("Forfait", "Plan"), d.plan ? pick(d.plan, "name", lang) : "—", "payment-plan")}
          {d.plan?.price_cad != null && row(L("Montant", "Amount"), `${money(d.plan.price_cad, lang)} CAD / ${L("mois", "month")} + ${L("taxes", "taxes")}`, "payment-amount")}
          {row(L("Début", "Start"), u.period_start ? fmtDateTime(u.period_start, lang) : "—", "payment-period-start")}
          {row(L("Prochain renouvellement", "Next renewal"), u.period_end ? fmtDateTime(u.period_end, lang) : "—", "payment-period-end")}
          {row(L("Heures incluses", "Included hours"), `${Number(u.monthly_allowance || 0)} h`, "payment-hours")}
        </div>
        {d.booking_state === "keycard_pending" && <KeycardPending contact={d.contact} />}
        <Link to={d.booking_state === "ok" ? "/member?tab=book" : "/member"} className="btn-red inline-block rounded-full px-6 py-3 font-semibold" data-testid="payment-back-btn">
          {d.booking_state === "ok" ? L("Réserver une séance au studio", "Book a Studio Session") : L("Ouvrir mon espace membre", "Open my member area")}</Link>
      </div>
    </div>
  );
}

export function PaymentSuccess() {
  const { t } = useLang();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const [state, setState] = useState("pending");

  useEffect(() => {
    const sid = params.get("session_id");
    let n = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setState("paid"); refresh(); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* retry */ }
      if (++n < 15) timer = setTimeout(poll, 2000); else setState("failed");
    };
    if (sid) poll(); else setState("failed");
    return () => clearTimeout(timer);
  }, [params, refresh]);

  if (state === "pending") return <Box testId="payment-pending" icon={<Loader2 className="animate-spin text-slate-300" size={40} />} text={t("pay.successTitle")} />;
  if (state === "paid") return <PaidDetails />;
  return <Box testId="payment-failed" icon={<XCircle className="text-red-500" size={40} />} text={t("pay.failed")} />;
}

export function PaymentCancel() {
  const { t } = useLang();
  useEffect(() => { api.post("/payments/cancel-pending").catch(() => {}); }, []);
  return <Box testId="payment-cancelled" icon={<XCircle className="text-slate-400" size={40} />} text={t("pay.cancel")} />;
}
